
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Languages, Save, RotateCcw, Edit } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  manualBackupTranslations, 
  restoreTranslationsFromBackup,
  updateTranslationKey 
} from '@/i18n/config';
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const LanguageSwitcher = ({ className }: { className?: string }) => {
  const { i18n, t } = useTranslation();
  const { toast } = useToast();
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [keyToUpdate, setKeyToUpdate] = useState('');
  const [enValue, setEnValue] = useState('');
  const [jaValue, setJaValue] = useState('');

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'ja', label: '日本語' },
  ];

  const handleLanguageChange = (langCode: string) => {
    console.log(`Changing language to: ${langCode}`);
    i18n.changeLanguage(langCode);
    // Add localStorage persistence for better user experience
    localStorage.setItem('i18nextLng', langCode);
  };

  const handleBackupTranslations = () => {
    const result = manualBackupTranslations();
    if (result.success) {
      toast({
        title: "Translations Backed Up",
        description: `All translations have been backed up successfully at ${new Date().toLocaleTimeString()}`,
      });
    }
  };

  const handleRestoreTranslations = () => {
    const result = restoreTranslationsFromBackup();
    if (result.success) {
      toast({
        title: "Translations Restored",
        description: "Translations have been restored from backup. Page will reload.",
      });
    } else {
      toast({
        title: "Restore Failed",
        description: result.error || "Failed to restore translations from backup.",
        variant: "destructive"
      });
    }
  };

  const handleUpdateTranslationKey = () => {
    if (!keyToUpdate || !enValue || !jaValue) {
      toast({
        title: "Missing Information",
        description: "Please provide the key and values for both languages.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Apply the update
      const updatedTranslations = updateTranslationKey(keyToUpdate, enValue, jaValue);
      
      // Update i18next resources with the new translation values
      i18n.addResourceBundle('en', 'translation', updatedTranslations.en, true, true);
      i18n.addResourceBundle('ja', 'translation', updatedTranslations.ja, true, true);
      
      // Close the dialog and show success message
      setIsUpdateDialogOpen(false);
      
      toast({
        title: "Translation Updated",
        description: `Key "${keyToUpdate}" has been updated for EN and JA.`,
      });
      
      // Reset form
      setKeyToUpdate('');
      setEnValue('');
      setJaValue('');
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update translation key.",
        variant: "destructive"
      });
    }
  };

  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={`h-9 w-9 px-0 ${className}`}
            type="button" // Explicitly set type to prevent form submission
          >
            <Languages className="h-4 w-4" />
            <span className="sr-only">{t('common.language')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`cursor-pointer ${i18n.language === lang.code ? 'font-medium text-primary' : ''}`}
            >
              {lang.label}
            </DropdownMenuItem>
          ))}

          {isDevelopment && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleBackupTranslations} className="cursor-pointer">
                <Save className="h-4 w-4 mr-2" />
                Backup Translations
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleRestoreTranslations} className="cursor-pointer">
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore from Backup
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setIsUpdateDialogOpen(true)} 
                className="cursor-pointer"
              >
                <Edit className="h-4 w-4 mr-2" />
                Update Translation Key
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Translation Key Update Dialog */}
      <Dialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Translation Key</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="key">Translation Key (dot notation)</Label>
              <Input
                id="key"
                placeholder="e.g., expenses.expenseTitle"
                value={keyToUpdate}
                onChange={(e) => setKeyToUpdate(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="en-value">English Value</Label>
              <Input
                id="en-value"
                placeholder="English translation"
                value={enValue}
                onChange={(e) => setEnValue(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ja-value">Japanese Value</Label>
              <Input
                id="ja-value"
                placeholder="Japanese translation"
                value={jaValue}
                onChange={(e) => setJaValue(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsUpdateDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleUpdateTranslationKey}>
              Update Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
